// SCR-STAFF-854 (AP-INS-02؛ US-INS-070 تا 072، 083): کسورات هر قلم با کد علت استاندارد، اعتراض با مستندات و ثبت نتیجه (کاهش
// کسورات و بازشدن مانده)، سیاست انتقال کسورات به بدهی مراجعه‌کننده و سوخت مانده باز. پیش‌فرض همه علت‌ها «هزینه مرکز» است
// (BR-INS-072)، پس انتقال فقط با علامت صریح مدیر انجام می‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsAppealView,
  type InsClaimItemView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  EmptyState,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { appealTone, claimKeys, claimTone, DEDUCTION_REASONS, type DeductionReason } from './claims';

export const SCR_STAFF_854 = 'SCR-STAFF-854';

export function DeductionsPage() {
  const { t } = useTranslation('ins');
  const access = useAccess();
  const write = useWriteAccess('ins.deduction.manage');
  const [tab, setTab] = useState<'items' | 'appeals' | 'policy'>('items');

  return (
    <ListPage
      screenId={SCR_STAFF_854}
      titleKey="ins:deductions.title"
      subtitleKey="ins:deductions.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Tabs value={tab} onChange={(_, value: 'items' | 'appeals' | 'policy') => setTab(value)}>
            <Tab value="items" label={t('ins:deductions.tab_items')} data-testid="ins-tab-deducted" />
            <Tab value="appeals" label={t('ins:deductions.tab_appeals')} data-testid="ins-tab-appeals" />
            <Tab value="policy" label={t('ins:deductions.tab_policy')} data-testid="ins-tab-policy" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'items' ? (
        <ItemsTab
          canDeduct={access.can('ins.deduction.manage') && write.canWrite}
          canAppeal={access.can('ins.appeal.manage') && write.canWrite}
          canWriteOff={access.can('ins.receivable.write_off') && write.canWrite}
        />
      ) : tab === 'appeals' ? (
        <AppealsTab canResolve={access.can('ins.appeal.manage') && write.canWrite} />
      ) : (
        <PolicyTab canManage={access.can('ins.deduction.manage') && write.canWrite} />
      )}
    </ListPage>
  );
}

/** اقلام ارسال‌شده با امکان ثبت کسورات، اعتراض و سوخت (US-INS-070، 071، 083). */
function ItemsTab({
  canDeduct,
  canAppeal,
  canWriteOff,
}: {
  canDeduct: boolean;
  canAppeal: boolean;
  canWriteOff: boolean;
}) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [deducting, setDeducting] = useState<InsClaimItemView | null>(null);
  const [appealing, setAppealing] = useState<InsClaimItemView | null>(null);
  const [writingOff, setWritingOff] = useState<InsClaimItemView | null>(null);

  const items = useQuery({
    queryKey: claimKeys.items(session?.tenantId, 'submitted'),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/insurance/claim-items', { params: { query: { status: 'Submitted' } } })),
  });

  const columns: DataTableColumn<InsClaimItemView>[] = [
    { id: 'patient', headerKey: 'ins:deductions.columns.patient', cell: (row) => row.patientName },
    { id: 'service', headerKey: 'ins:deductions.columns.service', cell: (row) => row.serviceName },
    { id: 'insurer', headerKey: 'ins:deductions.columns.insurer', cell: (row) => row.insurerName },
    { id: 'claimed', headerKey: 'ins:deductions.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    { id: 'deducted', headerKey: 'ins:deductions.columns.deducted', cell: (row) => <Money amountMinor={Number(row.deducted.amount)} />, align: 'right' },
    { id: 'open', headerKey: 'ins:deductions.columns.open', cell: (row) => <Money amountMinor={Number(row.open.amount)} />, align: 'right' },
    {
      id: 'reasons',
      headerKey: 'ins:deductions.columns.reason',
      cell: (row) => (
        <Stack spacing={0.25} data-testid={`ins-deductions-${row.claimItemId}`}>
          {row.deductions.length === 0
            ? '—'
            : row.deductions.map((deduction) => (
                <Typography key={deduction.deductionId} variant="body2">
                  {t(`ins:deduction_reason.${deduction.reasonCode}`)}
                  {deduction.transferredToPatient ? ` · ${t('ins:deductions.transferred')}` : ''}
                </Typography>
              ))}
        </Stack>
      ),
    },
    {
      id: 'deadline',
      headerKey: 'ins:deductions.columns.deadline',
      cell: (row) => {
        const deadline = row.deductions.find((deduction) => deduction.appealDeadlineOn !== null)?.appealDeadlineOn;
        return deadline ? <JalaliDate value={deadline} /> : '—';
      },
    },
    {
      id: 'status',
      headerKey: 'ins:deductions.columns.status',
      cell: (row) => <StatusChip tone={claimTone(row.status)} labelKey={`ins:claim_status.${row.status}`} />,
    },
    {
      id: 'actions',
      headerKey: 'ins:deductions.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {canDeduct && Number(row.open.amount) > 0 ? (
            <Button size="small" onClick={() => setDeducting(row)} data-testid={`ins-deduct-${row.claimItemId}`}>
              {t('ins:deductions.record')}
            </Button>
          ) : null}
          {canAppeal && row.deductions.some((deduction) => Number(deduction.net.amount) > 0) ? (
            <Button size="small" onClick={() => setAppealing(row)} data-testid={`ins-appeal-${row.claimItemId}`}>
              {t('ins:deductions.appeal')}
            </Button>
          ) : null}
          {canWriteOff && Number(row.open.amount) > 0 ? (
            <Button size="small" color="warning" onClick={() => setWritingOff(row)} data-testid={`ins-write-off-${row.claimItemId}`}>
              {t('ins:deductions.write_off')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <DataTable
        captionKey="ins:deductions.caption"
        columns={columns}
        rows={items.data}
        getRowId={(row) => row.claimItemId}
        isLoading={items.isPending}
        error={items.error}
        onRetry={() => void items.refetch()}
        emptyTitleKey="ins:deductions.empty"
      />
      {deducting && <DeductionDialog item={deducting} onClose={() => setDeducting(null)} />}
      {appealing && <AppealDialog item={appealing} onClose={() => setAppealing(null)} />}
      {writingOff && <WriteOffDialog item={writingOff} onClose={() => setWritingOff(null)} />}
    </>
  );
}

const deductionSchema = z.object({
  amount: z.number().int().positive('ins:deductions.amount'),
  reasonCode: z.string().min(1, 'common:validation.required'),
  reasonText: z.string().max(300),
  recordedOn: z.string(),
});

type DeductionForm = z.input<typeof deductionSchema>;

/** ثبت کسورات (AC-INS-070.1): بیش از مانده باز با PHX-INS-0208 رد می‌شود (BR-INS-070). */
function DeductionDialog({ item, onClose }: { item: InsClaimItemView; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<DeductionForm>({
    resolver: zodResolver(deductionSchema),
    defaultValues: { amount: 0, reasonCode: 'FeeScheduleDifference', reasonText: '', recordedOn: '' },
  });

  const save = useMutation({
    mutationFn: (values: DeductionForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-items/{id}:record-deduction', {
          params: { path: { id: item.claimItemId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            amount: Number(values.amount),
            reasonCode: values.reasonCode as DeductionReason,
            reasonText: values.reasonText === '' ? null : values.reasonText,
            recordedOn: values.recordedOn === '' ? null : values.recordedOn,
          },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, {
        fields: ['amount', 'reasonCode', 'reasonText'],
        codeFields: { 'PHX-INS-0208': 'amount' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_854}
      titleKey="ins:deductions.record_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <MoneyFieldRHF control={form.control} name="amount" labelKey="ins:deductions.amount" fullWidth autoFocus />
      <SelectRHF
        control={form.control}
        name="reasonCode"
        labelKey="ins:deductions.reason"
        options={DEDUCTION_REASONS.map((value) => ({ value, labelKey: `ins:deduction_reason.${value}` }))}
        fullWidth
      />
      <TextFieldRHF control={form.control} name="reasonText" labelKey="ins:deductions.reason_text" fullWidth multiline minRows={2} />
      <JalaliDatePickerRHF control={form.control} name="recordedOn" labelKey="ins:deductions.recorded_on" />
    </FormDialog>
  );
}

const appealSchema = z.object({ reason: zRequiredText(1_000) });
type AppealForm = z.input<typeof appealSchema>;

/** ثبت اعتراض به کسورات باز قلم (AC-INS-071.1؛ بیرون مهلت ← PHX-INS-0210). */
function AppealDialog({ item, onClose }: { item: InsClaimItemView; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<AppealForm>({ resolver: zodResolver(appealSchema), defaultValues: { reason: '' } });
  const open = item.deductions.filter((deduction) => Number(deduction.net.amount) > 0);

  const save = useMutation({
    mutationFn: (values: AppealForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/appeals', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            insurerId: item.insurerId,
            branchId: item.branchId,
            reason: values.reason,
            documentIds: null,
            items: open.map((deduction) => ({ deductionId: deduction.deductionId, disputedAmount: null })),
          },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'appeals'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['reason'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_854}
      titleKey="ins:deductions.appeal_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <TextFieldRHF control={form.control} name="reason" labelKey="ins:deductions.appeal_reason" fullWidth multiline minRows={3} autoFocus />
    </FormDialog>
  );
}

const writeOffSchema = z.object({ reason: zRequiredText(500) });
type WriteOffForm = z.input<typeof writeOffSchema>;

/** سوخت مانده باز (AC-INS-083.1؛ تأیید مدیر با Permission `ins.receivable.write_off`). */
function WriteOffDialog({ item, onClose }: { item: InsClaimItemView; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<WriteOffForm>({ resolver: zodResolver(writeOffSchema), defaultValues: { reason: '' } });

  const save = useMutation({
    mutationFn: (values: WriteOffForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/claim-items/{id}:write-off', {
          params: { path: { id: item.claimItemId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { reason: values.reason, writtenOffOn: null },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['reason'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_854}
      titleKey="ins:deductions.write_off_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
      submitKey="ins:deductions.write_off"
    >
      <Typography variant="body2" data-testid="ins-write-off-amount">
        <Money amountMinor={Number(item.open.amount)} />
      </Typography>
      <TextFieldRHF control={form.control} name="reason" labelKey="ins:deductions.write_off_reason" fullWidth multiline minRows={2} autoFocus />
    </FormDialog>
  );
}

/** اعتراض‌ها با ثبت نتیجه بیمه‌گر (AC-INS-071.1). */
function AppealsTab({ canResolve }: { canResolve: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [resolving, setResolving] = useState<InsAppealView | null>(null);

  const appeals = useQuery({
    queryKey: claimKeys.appeals(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/appeals', { params: { query: {} } })),
  });

  const columns: DataTableColumn<InsAppealView>[] = [
    { id: 'insurer', headerKey: 'ins:deductions.columns.insurer', cell: (row) => row.insurerName },
    {
      id: 'status',
      headerKey: 'ins:deductions.columns.status',
      cell: (row) => <StatusChip tone={appealTone(row.status)} labelKey={`ins:appeal_status.${row.status}`} />,
    },
    { id: 'disputed', headerKey: 'ins:deductions.columns.disputed', cell: (row) => <Money amountMinor={Number(row.disputed.amount)} />, align: 'right' },
    { id: 'recovered', headerKey: 'ins:deductions.columns.recovered', cell: (row) => <Money amountMinor={Number(row.recovered.amount)} />, align: 'right' },
    {
      id: 'submitted_on',
      headerKey: 'ins:deductions.columns.submitted_on',
      cell: (row) => (row.submittedOn ? <JalaliDate value={row.submittedOn} /> : '—'),
    },
    {
      id: 'reason',
      headerKey: 'ins:deductions.columns.reason',
      cell: (row) => (
        <Stack spacing={0.25}>
          {row.items.map((appealItem) => (
            <Typography key={appealItem.appealItemId} variant="body2">
              {t(`ins:deduction_reason.${appealItem.reasonCode}`)}
            </Typography>
          ))}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'ins:deductions.columns.actions',
      cell: (row) =>
        canResolve && row.status === 'Submitted' ? (
          <Button size="small" onClick={() => setResolving(row)} data-testid={`ins-appeal-resolve-${row.appealId}`}>
            {t('ins:deductions.resolve')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        captionKey="ins:deductions.appeals_caption"
        columns={columns}
        rows={appeals.data}
        getRowId={(row) => row.appealId}
        isLoading={appeals.isPending}
        error={appeals.error}
        onRetry={() => void appeals.refetch()}
        emptyTitleKey="ins:deductions.appeals_empty"
      />
      {resolving && <ResolveDialog appeal={resolving} onClose={() => setResolving(null)} />}
    </>
  );
}

const resolveSchema = z.object({ recovered: z.number().int().min(0), note: z.string().max(1_000) });
type ResolveForm = z.input<typeof resolveSchema>;

/** ثبت نتیجه اعتراض: مبلغ برگشتی کسورات را کاهش و مانده باز را همان اندازه زیاد می‌کند (AC-INS-071.1). */
function ResolveDialog({ appeal, onClose }: { appeal: InsAppealView; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<ResolveForm>({ resolver: zodResolver(resolveSchema), defaultValues: { recovered: 0, note: '' } });

  const save = useMutation({
    mutationFn: (values: ResolveForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/appeals/{id}:resolve', {
          params: { path: { id: appeal.appealId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            items: appeal.items.map((item) => ({ appealItemId: item.appealItemId, recoveredAmount: Number(values.recovered) })),
            note: values.note === '' ? null : values.note,
            resolvedOn: null,
          },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'appeals'] });
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['recovered', 'note'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_854}
      titleKey="ins:deductions.resolve_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <MoneyFieldRHF control={form.control} name="recovered" labelKey="ins:deductions.recovered" fullWidth autoFocus />
      <TextFieldRHF control={form.control} name="note" labelKey="ins:deductions.note" fullWidth multiline minRows={2} />
    </FormDialog>
  );
}

/** سیاست انتقال کسورات به مراجعه‌کننده (AC-INS-072.1؛ پیش‌فرض هیچ علتی منتقل نمی‌شود — BR-INS-072). */
function PolicyTab({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[] | null>(null);

  const policy = useQuery({
    queryKey: claimKeys.policy(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/deduction-policy', {})),
  });

  const save = useMutation({
    mutationFn: (transferReasonCodes: string[]) =>
      unwrap(
        tenantApi.PUT('/api/v1/insurance/deduction-policy', {
          body: { transferReasonCodes: transferReasonCodes as DeductionReason[] },
        }),
      ),
    onSuccess: async () => {
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: claimKeys.policy(session?.tenantId) });
    },
  });

  if (policy.isPending) {
    return <EmptyState titleKey="common:states.loading" />;
  }

  const current: string[] = selected ?? (policy.data?.transferReasonCodes ?? []).map((code) => String(code));

  return (
    <Stack spacing={2}>
      <Alert severity="info" data-testid="ins-policy-hint">
        {t('ins:deductions.policy_hint')}
      </Alert>
      <Stack data-testid="ins-policy-list">
        {DEDUCTION_REASONS.map((reason) => (
          <FormControlLabel
            key={reason}
            disabled={!canManage}
            control={
              <Checkbox
                checked={current.includes(reason)}
                onChange={(event) =>
                  setSelected(
                    event.target.checked ? [...current, reason] : current.filter((value) => value !== reason),
                  )
                }
                data-testid={`ins-policy-${reason}`}
              />
            }
            label={t(`ins:deduction_reason.${reason}`)}
          />
        ))}
      </Stack>
      {canManage ? (
        <Button
          variant="contained"
          sx={{ alignSelf: 'flex-start' }}
          disabled={save.isPending || selected === null}
          onClick={() => save.mutate(current)}
          data-testid="ins-policy-save"
        >
          {t('ins:deductions.policy_save')}
        </Button>
      ) : null}
    </Stack>
  );
}
